import { Dropdown as DropdownPrime } from 'primereact/dropdown'
import { Skeleton } from 'primereact/skeleton'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useController } from 'react-hook-form'
import PropTypes from 'prop-types'

export const Dropdown = ({
  name, // Name of the input element.
  value, // Value of the component.
  defaultValue,
  onChange, //
  options, // An array of selectitems to display as the available options.
  optionLabel, // Name of the label field of an option when arbitrary objects are used as options instead of SelectItems.
  optionValue, // Name of the value field of an option when arbitrary objects are used as options instead of SelectItems.
  placeholder, // Default text to display when no option is selected.
  dropdownIcon, // Icon of the dropdown arrow.
  editable, // When present, custom value instead of predefined options can be entered using the editable input field.
  loading, //
  filter, // When specified, displays an input field to filter the items on keyup.
  filterIcon, // Icon of the filter to search.
  filterPlaceholder, // Placeholder text to show when filter input is empty.
  scrollHeight, // Height of the viewport in pixels, a scrollbar is defined if height of list exceeds this value.
  height, //
  rules, // Rules for required input.
  readOnly, // When present, it specifies that the value cannot be changed.
  disabled, // When present, it specifies that the component should be disabled.
  invalidClassName, // Style class for invalid input.
  invalidMessageClassName, //
  panelClassName, // Style class of the overlay panel element.
  className, // Style class of the component.
  style, // Inline style of the element.
  itemTemplate, // The template of items.
  valueTemplate, // The template of selected item.
  focus, //
  blur, //
  appendTo,
  // onClick
  onInput,
  filterInputAutoFocus,
  onFilter,
  autoOptionFocus,
  reference,
  autoFocus,
  tabIndex
}) => {
  const { field, fieldState: { error } } = useController({
    name,
    rules,
    defaultValue
  })

  const handleOnChange = useCallback((e) => {
    if (e.value?.id !== undefined && e.value !== null) {
      const text = e.value[optionLabel]
      const value = {
        id: e.value.id,
        [optionLabel]: text
      }
      onChange(e)
      field.onChange(value)
      onInput({
        target: {
          value: text
        }
      })
    }
  }, [field, onChange, optionLabel])

  const handleOnInput = useCallback(
    (e) => {
      onInput(e)
    },
    [onInput]
  )

  useEffect(() => {
    if (value?.id !== undefined && value !== null) {
      handleOnChange({ value })
    }
  }, [value])

  return (
    loading
      ? <Skeleton width='100%' height={height} />
      : (
        <>
          <DropdownPrime
            ref={reference}
            inputRef={reference}
            autoFocus={autoFocus}
            tabIndex={tabIndex}
            name={name}
            value={field.value}
            options={options.length > 0
              ? options.map(item => {
                return {
                  id: item.id,
                  [optionLabel]: item[optionLabel]
                }
              })
              : []}
            optionLabel={optionLabel}
            optionValue={optionValue}
            placeholder={placeholder}
            dropdownIcon={dropdownIcon}
            editable={editable}
            filter={filter}
            filterIcon={filterIcon}
            resetFilterOnHide
            filterPlaceholder={filterPlaceholder}
            disabled={disabled || readOnly}
            panelClassName={panelClassName}
            className={`${className} ${error ? invalidClassName : ''}`}
            style={style}
            {...field}
            onChange={handleOnChange}
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            onFocus={focus}
            onBlur={blur}
            onInput={handleOnInput}
            appendTo={appendTo}
            scrollHeight={scrollHeight ?? '200px'}
            onFilter={onFilter}
            autoOptionFocus={autoOptionFocus}
          />
          {error && <span className={invalidMessageClassName}>{error.message}</span>}
        </>)
  )
}

Dropdown.defaultProps = {
  name: '',
  value: {},
  onChange: () => { },
  placeholder: '',
  loading: false,
  filter: false,
  readOnly: false,
  disabled: false,
  invalidClassName: '',
  invalidMessageClassName: '',
  className: '',
  optionLabel: 'name',
  options: [],
  onInput: () => {}
}

Dropdown.propTypes = {
  name: PropTypes.string,
  value: PropTypes.object,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  filter: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  invalidClassName: PropTypes.string,
  invalidMessageClassName: PropTypes.string,
  className: PropTypes.string
}
